import type { Transaction, Auth } from 'oscar-typedef'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  type Account,
  type ITransactionExecutor,
  type EncUVD,
  SolanaPrepareData,
  type SolanaSendExtraData,
} from '@/types'

export class SolanaCoinTransactionExecutor implements ITransactionExecutor {
  private account: Account

  constructor(params: { account: Account }) {
    this.account = params.account
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<SolanaPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.account.id,
          to: params.to,
          amount: params.amount,
        },
      )
    if (!data.detail) {
      throw new Error('No detail data for prepare')
    }
    return new SolanaPrepareData({
      blockhash: data.detail.blockhash,
      lamportsPerSignature: data.detail.lamportsPerSignature,
      lastValidBlockHeight: data.detail.lastValidBlockHeight,
      priorityFee: data.detail.priorityFee,
      rentFee: data.detail.rentFee,
      keepRentFee: data.detail.keepRentFee,
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: SolanaSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.account.walletId,
      accountId: this.account.id,
      pin: params.encUVD,
      txData: {
        txCommand: 'transfer',
        to: params.to,
        from: this.account.address,
        amount: params.amount,
        blockhash: params.extraData.blockhash,
        lastValidBlockHeight: params.extraData.lastValidBlockHeight,
        lamportsPerSignature: params.extraData.lamportsPerSignature,
        priorityFee: params.extraData.priorityFee,
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    // @TODO Solana broadcast
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.account.walletId,
        accountId: this.account.id,
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
      },
    )
  }
}
